/**
 * StorageManager.js
 * 
 * Handles all client-side data storage operations using IndexedDB.
 * This is the primary storage mechanism for the application, ensuring
 * all project data remains in the browser.
 */

class StorageManager {
  constructor() {
    this.dbName = 'AICodeImplementerDB';
    this.version = 1;
    this.db = null;
  }

  /**
   * Initialize the database with all required object stores
   * @returns {Promise} Resolves when the database is ready
   */
  async initDatabase() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        // Create stores with indices for efficient queries
        const projectStore = db.createObjectStore('projects', { keyPath: 'id' });
        projectStore.createIndex('byName', 'name', { unique: false });
        projectStore.createIndex('byUpdated', 'updated', { unique: false });
        
        const filesStore = db.createObjectStore('files', { keyPath: 'id' });
        filesStore.createIndex('byProjectId', 'projectId', { unique: false });
        filesStore.createIndex('byPath', 'path', { unique: false });
        
        const fileContentsStore = db.createObjectStore('fileContents', { keyPath: 'id' });
        fileContentsStore.createIndex('byFileId', 'fileId', { unique: false });
        fileContentsStore.createIndex('byVersion', 'version', { unique: false });
        
        db.createObjectStore('components', { keyPath: 'id' });
        db.createObjectStore('batches', { keyPath: 'id' });
        db.createObjectStore('prompts', { keyPath: 'id' });
        db.createObjectStore('responses', { keyPath: 'id' });
      };
      
      request.onsuccess = (event) => {
        this.db = event.target.result;
        resolve(this.db);
      };
      
      request.onerror = (event) => {
        reject(`Database error: ${event.target.errorCode}`);
      };
    });
  }

  /**
   * Create a new project
   * @param {Object} project - Project data (without id)
   * @returns {Promise<string>} - Project ID
   */
  async createProject(project) {
    await this.ensureDbConnection();
    
    const transaction = this.db.transaction(['projects'], 'readwrite');
    const store = transaction.objectStore('projects');
    
    const projectWithMeta = {
      ...project,
      id: crypto.randomUUID(),
      created: Date.now(),
      updated: Date.now()
    };
    
    return new Promise((resolve, reject) => {
      const request = store.add(projectWithMeta);
      
      request.onsuccess = () => resolve(projectWithMeta.id);
      request.onerror = (e) => reject(`Failed to create project: ${e.target.error}`);
    });
  }

  /**
   * Get a project by ID
   * @param {string} id - Project ID
   * @returns {Promise<Object>} - Project data
   */
  async getProject(id) {
    await this.ensureDbConnection();
    
    const transaction = this.db.transaction(['projects'], 'readonly');
    const store = transaction.objectStore('projects');
    
    return new Promise((resolve, reject) => {
      const request = store.get(id);
      
      request.onsuccess = () => resolve(request.result);
      request.onerror = (e) => reject(`Failed to get project: ${e.target.error}`);
    });
  }

  /**
   * Update an existing project
   * @param {Object} project - Project data with ID
   * @returns {Promise<string>} - Project ID
   */
  async updateProject(project) {
    await this.ensureDbConnection();
    
    const transaction = this.db.transaction(['projects'], 'readwrite');
    const store = transaction.objectStore('projects');
    
    const updatedProject = {
      ...project,
      updated: Date.now()
    };
    
    return new Promise((resolve, reject) => {
      const request = store.put(updatedProject);
      
      request.onsuccess = () => resolve(updatedProject.id);
      request.onerror = (e) => reject(`Failed to update project: ${e.target.error}`);
    });
  }

  /**
   * Get all projects, sorted by last updated
   * @returns {Promise<Array>} - Array of projects
   */
  async getAllProjects() {
    await this.ensureDbConnection();
    
    const transaction = this.db.transaction(['projects'], 'readonly');
    const store = transaction.objectStore('projects');
    const index = store.index('byUpdated');
    
    return new Promise((resolve, reject) => {
      const request = index.getAll();
      
      request.onsuccess = () => {
        // Sort by updated timestamp, newest first
        const projects = request.result.sort((a, b) => b.updated - a.updated);
        resolve(projects);
      };
      request.onerror = (e) => reject(`Failed to get projects: ${e.target.error}`);
    });
  }

  /**
   * Delete a project and all its associated data
   * @param {string} id - Project ID
   * @returns {Promise<boolean>} - Success indicator
   */
  async deleteProject(id) {
    await this.ensureDbConnection();
    
    // Get all files for this project to delete their contents
    const files = await this.getFilesByProject(id);
    const fileIds = files.map(file => file.id);
    
    // Start a transaction that encompasses all stores
    const transaction = this.db.transaction(
      ['projects', 'files', 'fileContents', 'components', 'batches', 'prompts', 'responses'],
      'readwrite'
    );
    
    // Delete from each store
    const projectStore = transaction.objectStore('projects');
    const filesStore = transaction.objectStore('files');
    const fileContentsStore = transaction.objectStore('fileContents');
    const componentsStore = transaction.objectStore('components');
    const batchesStore = transaction.objectStore('batches');
    const promptsStore = transaction.objectStore('prompts');
    const responsesStore = transaction.objectStore('responses');
    
    return new Promise((resolve, reject) => {
      // Delete project
      projectStore.delete(id);
      
      // Delete all files for this project
      const index = filesStore.index('byProjectId');
      const filesRequest = index.getAllKeys(id);
      
      filesRequest.onsuccess = () => {
        const fileKeys = filesRequest.result;
        fileKeys.forEach(fileKey => {
          filesStore.delete(fileKey);
        });
      };
      
      // Delete file contents
      for (const fileId of fileIds) {
        const contentIndex = fileContentsStore.index('byFileId');
        const contentsRequest = contentIndex.getAllKeys(fileId);
        
        contentsRequest.onsuccess = () => {
          const contentKeys = contentsRequest.result;
          contentKeys.forEach(contentKey => {
            fileContentsStore.delete(contentKey);
          });
        };
      }
      
      // Delete components
      const componentsRequest = componentsStore.getAll();
      componentsRequest.onsuccess = () => {
        const components = componentsRequest.result;
        components.filter(c => c.projectId === id).forEach(component => {
          componentsStore.delete(component.id);
        });
      };
      
      // Delete batches
      const batchesRequest = batchesStore.getAll();
      batchesRequest.onsuccess = () => {
        const batches = batchesRequest.result;
        batches.filter(b => b.projectId === id).forEach(batch => {
          batchesStore.delete(batch.id);
        });
      };
      
      // Delete prompts and responses (assuming they have projectId field)
      // This could be enhanced with proper indices
      const promptsRequest = promptsStore.getAll();
      promptsRequest.onsuccess = () => {
        const prompts = promptsRequest.result;
        prompts.filter(p => p.projectId === id).forEach(prompt => {
          promptsStore.delete(prompt.id);
        });
      };
      
      const responsesRequest = responsesStore.getAll();
      responsesRequest.onsuccess = () => {
        const responses = responsesRequest.result;
        responses.filter(r => r.projectId === id).forEach(response => {
          responsesStore.delete(response.id);
        });
      };
      
      transaction.oncomplete = () => resolve(true);
      transaction.onerror = (e) => reject(`Failed to delete project: ${e.target.error}`);
    });
  }

  /**
   * Save a file (create or update)
   * @param {Object} file - File data
   * @returns {Promise<string>} - File ID
   */
  async saveFile(file) {
    await this.ensureDbConnection();
    
    const transaction = this.db.transaction(['files'], 'readwrite');
    const store = transaction.objectStore('files');
    
    // If file doesn't have an ID, create one
    if (!file.id) {
      file.id = crypto.randomUUID();
    }
    
    return new Promise((resolve, reject) => {
      const request = store.put(file);
      
      request.onsuccess = () => resolve(file.id);
      request.onerror = (e) => reject(`Failed to save file: ${e.target.error}`);
    });
  }

  /**
   * Get a file by ID
   * @param {string} id - File ID
   * @returns {Promise<Object>} - File data
   */
  async getFile(id) {
    await this.ensureDbConnection();
    
    const transaction = this.db.transaction(['files'], 'readonly');
    const store = transaction.objectStore('files');
    
    return new Promise((resolve, reject) => {
      const request = store.get(id);
      
      request.onsuccess = () => resolve(request.result);
      request.onerror = (e) => reject(`Failed to get file: ${e.target.error}`);
    });
  }

  /**
   * Get a file by path within a project
   * @param {string} projectId - Project ID
   * @param {string} path - File path
   * @returns {Promise<Object>} - File data
   */
  async getFileByPath(projectId, path) {
    await this.ensureDbConnection();
    
    const transaction = this.db.transaction(['files'], 'readonly');
    const store = transaction.objectStore('files');
    
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      
      request.onsuccess = () => {
        const file = request.result.find(f => 
          f.projectId === projectId && f.path === path
        );
        resolve(file || null);
      };
      request.onerror = (e) => reject(`Failed to get file by path: ${e.target.error}`);
    });
  }

  /**
   * Get all files for a project
   * @param {string} projectId - Project ID
   * @returns {Promise<Array>} - Array of files
   */
  async getFilesByProject(projectId) {
    await this.ensureDbConnection();
    
    const transaction = this.db.transaction(['files'], 'readonly');
    const store = transaction.objectStore('files');
    const index = store.index('byProjectId');
    
    return new Promise((resolve, reject) => {
      const request = index.getAll(projectId);
      
      request.onsuccess = () => resolve(request.result);
      request.onerror = (e) => reject(`Failed to get files: ${e.target.error}`);
    });
  }

  /**
   * Save file content as a new version
   * @param {string} fileId - File ID
   * @param {string} content - File content
   * @param {number} version - Version number (optional)
   * @returns {Promise<string>} - Content ID
   */
  async saveFileContent(fileId, content, version) {
    await this.ensureDbConnection();
    
    const transaction = this.db.transaction(['fileContents'], 'readwrite');
    const store = transaction.objectStore('fileContents');
    
    // If no version provided, get the latest and increment
    if (!version) {
      const latestVersion = await this.getLatestFileVersion(fileId);
      version = latestVersion ? latestVersion.version + 1 : 1;
    }
    
    const fileContent = {
      id: crypto.randomUUID(),
      fileId,
      content,
      version,
      timestamp: Date.now()
    };
    
    return new Promise((resolve, reject) => {
      const request = store.add(fileContent);
      
      request.onsuccess = () => resolve(fileContent.id);
      request.onerror = (e) => reject(`Failed to save file content: ${e.target.error}`);
    });
  }

  /**
   * Get file content by ID or latest version
   * @param {string} fileId - File ID
   * @param {number} version - Specific version (optional)
   * @returns {Promise<Object>} - File content with metadata
   */
  async getFileContent(fileId, version) {
    await this.ensureDbConnection();
    
    if (version) {
      return this.getFileContentByVersion(fileId, version);
    } else {
      return this.getLatestFileContent(fileId);
    }
  }

  /**
   * Get file content by specific version
   * @param {string} fileId - File ID
   * @param {number} version - Version number
   * @returns {Promise<Object>} - File content with metadata
   */
  async getFileContentByVersion(fileId, version) {
    const transaction = this.db.transaction(['fileContents'], 'readonly');
    const store = transaction.objectStore('fileContents');
    
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      
      request.onsuccess = () => {
        const content = request.result.find(c => 
          c.fileId === fileId && c.version === version
        );
        resolve(content || null);
      };
      request.onerror = (e) => reject(`Failed to get file content: ${e.target.error}`);
    });
  }

  /**
   * Get the latest version of file content
   * @param {string} fileId - File ID
   * @returns {Promise<Object>} - File content with metadata
   */
  async getLatestFileContent(fileId) {
    const transaction = this.db.transaction(['fileContents'], 'readonly');
    const store = transaction.objectStore('fileContents');
    const index = store.index('byFileId');
    
    return new Promise((resolve, reject) => {
      const request = index.getAll(fileId);
      
      request.onsuccess = () => {
        if (request.result.length === 0) {
          resolve(null);
          return;
        }
        
        // Find the one with the highest version number
        const latest = request.result.reduce((prev, current) => {
          return (prev.version > current.version) ? prev : current;
        });
        
        resolve(latest);
      };
      request.onerror = (e) => reject(`Failed to get latest file content: ${e.target.error}`);
    });
  }

  /**
   * Get the latest version number for a file
   * @param {string} fileId - File ID
   * @returns {Promise<Object>} - Version object with number
   */
  async getLatestFileVersion(fileId) {
    const latest = await this.getLatestFileContent(fileId);
    return latest;
  }

  /**
   * Get all versions of a file
   * @param {string} fileId - File ID
   * @returns {Promise<Array>} - Array of file content versions
   */
  async getAllFileVersions(fileId) {
    await this.ensureDbConnection();
    
    const transaction = this.db.transaction(['fileContents'], 'readonly');
    const store = transaction.objectStore('fileContents');
    const index = store.index('byFileId');
    
    return new Promise((resolve, reject) => {
      const request = index.getAll(fileId);
      
      request.onsuccess = () => {
        // Sort by version number, descending
        const versions = request.result.sort((a, b) => b.version - a.version);
        resolve(versions);
      };
      request.onerror = (e) => reject(`Failed to get file versions: ${e.target.error}`);
    });
  }

  /**
   * Save a component
   * @param {Object} component - Component data
   * @returns {Promise<string>} - Component ID
   */
  async saveComponent(component) {
    await this.ensureDbConnection();
    
    const transaction = this.db.transaction(['components'], 'readwrite');
    const store = transaction.objectStore('components');
    
    if (!component.id) {
      component.id = crypto.randomUUID();
    }
    
    return new Promise((resolve, reject) => {
      const request = store.put(component);
      
      request.onsuccess = () => resolve(component.id);
      request.onerror = (e) => reject(`Failed to save component: ${e.target.error}`);
    });
  }

  /**
   * Get a component by ID
   * @param {string} id - Component ID
   * @returns {Promise<Object>} - Component data
   */
  async getComponent(id) {
    await this.ensureDbConnection();
    
    const transaction = this.db.transaction(['components'], 'readonly');
    const store = transaction.objectStore('components');
    
    return new Promise((resolve, reject) => {
      const request = store.get(id);
      
      request.onsuccess = () => resolve(request.result);
      request.onerror = (e) => reject(`Failed to get component: ${e.target.error}`);
    });
  }

  /**
   * Get all components for a project
   * @param {string} projectId - Project ID
   * @returns {Promise<Array>} - Array of components
   */
  async getComponentsByProject(projectId) {
    await this.ensureDbConnection();
    
    const transaction = this.db.transaction(['components'], 'readonly');
    const store = transaction.objectStore('components');
    
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      
      request.onsuccess = () => {
        const components = request.result.filter(c => c.projectId === projectId);
        resolve(components);
      };
      request.onerror = (e) => reject(`Failed to get components: ${e.target.error}`);
    });
  }

  /**
   * Save a batch
   * @param {Object} batch - Batch data
   * @returns {Promise<string>} - Batch ID
   */
  async saveBatch(batch) {
    await this.ensureDbConnection();
    
    const transaction = this.db.transaction(['batches'], 'readwrite');
    const store = transaction.objectStore('batches');
    
    if (!batch.id) {
      batch.id = crypto.randomUUID();
    }
    
    return new Promise((resolve, reject) => {
      const request = store.put(batch);
      
      request.onsuccess = () => resolve(batch.id);
      request.onerror = (e) => reject(`Failed to save batch: ${e.target.error}`);
    });
  }

  /**
   * Get a batch by ID
   * @param {string} id - Batch ID
   * @returns {Promise<Object>} - Batch data
   */
  async getBatch(id) {
    await this.ensureDbConnection();
    
    const transaction = this.db.transaction(['batches'], 'readonly');
    const store = transaction.objectStore('batches');
    
    return new Promise((resolve, reject) => {
      const request = store.get(id);
      
      request.onsuccess = () => resolve(request.result);
      request.onerror = (e) => reject(`Failed to get batch: ${e.target.error}`);
    });
  }

  /**
   * Update batch status
   * @param {string} id - Batch ID
   * @param {string} status - New status
   * @returns {Promise<Object>} - Updated batch
   */
  async updateBatchStatus(id, status) {
    const batch = await this.getBatch(id);
    if (!batch) {
      throw new Error(`Batch ${id} not found`);
    }
    
    batch.status = status;
    await this.saveBatch(batch);
    return batch;
  }

  /**
   * Get all batches for a project
   * @param {string} projectId - Project ID
   * @returns {Promise<Array>} - Array of batches
   */
  async getBatchesByProject(projectId) {
    await this.ensureDbConnection();
    
    const transaction = this.db.transaction(['batches'], 'readonly');
    const store = transaction.objectStore('batches');
    
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      
      request.onsuccess = () => {
        const batches = request.result.filter(b => b.projectId === projectId);
        resolve(batches);
      };
      request.onerror = (e) => reject(`Failed to get batches: ${e.target.error}`);
    });
  }

  /**
   * Save a prompt
   * @param {Object} prompt - Prompt data
   * @returns {Promise<string>} - Prompt ID
   */
  async savePrompt(prompt) {
    await this.ensureDbConnection();
    
    const transaction = this.db.transaction(['prompts'], 'readwrite');
    const store = transaction.objectStore('prompts');
    
    if (!prompt.id) {
      prompt.id = crypto.randomUUID();
    }
    
    // Add timestamp
    prompt.timestamp = Date.now();
    
    return new Promise((resolve, reject) => {
      const request = store.put(prompt);
      
      request.onsuccess = () => resolve(prompt.id);
      request.onerror = (e) => reject(`Failed to save prompt: ${e.target.error}`);
    });
  }

  /**
   * Get prompts for a project
   * @param {string} projectId - Project ID
   * @returns {Promise<Array>} - Array of prompts
   */
  async getPromptsByProject(projectId) {
    await this.ensureDbConnection();
    
    const transaction = this.db.transaction(['prompts'], 'readonly');
    const store = transaction.objectStore('prompts');
    
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      
      request.onsuccess = () => {
        const prompts = request.result.filter(p => p.projectId === projectId);
        // Sort by timestamp, newest first
        resolve(prompts.sort((a, b) => b.timestamp - a.timestamp));
      };
      request.onerror = (e) => reject(`Failed to get prompts: ${e.target.error}`);
    });
  }

  /**
   * Save a response
   * @param {Object} response - Response data
   * @returns {Promise<string>} - Response ID
   */
  async saveResponse(response) {
    await this.ensureDbConnection();
    
    const transaction = this.db.transaction(['responses'], 'readwrite');
    const store = transaction.objectStore('responses');
    
    if (!response.id) {
      response.id = crypto.randomUUID();
    }
    
    // Add timestamp
    response.timestamp = Date.now();
    
    return new Promise((resolve, reject) => {
      const request = store.put(response);
      
      request.onsuccess = () => resolve(response.id);
      request.onerror = (e) => reject(`Failed to save response: ${e.target.error}`);
    });
  }

  /**
   * Get responses for a project
   * @param {string} projectId - Project ID
   * @returns {Promise<Array>} - Array of responses
   */
  async getResponsesByProject(projectId) {
    await this.ensureDbConnection();
    
    const transaction = this.db.transaction(['responses'], 'readonly');
    const store = transaction.objectStore('responses');
    
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      
      request.onsuccess = () => {
        const responses = request.result.filter(r => r.projectId === projectId);
        // Sort by timestamp, newest first
        resolve(responses.sort((a, b) => b.timestamp - a.timestamp));
      };
      request.onerror = (e) => reject(`Failed to get responses: ${e.target.error}`);
    });
  }

  /**
   * Export a project and all its associated data
   * @param {string} projectId - Project ID
   * @returns {Promise<Blob>} - JSON blob for download
   */
  async exportProject(projectId) {
    await this.ensureDbConnection();
    
    // Gather all project-related data
    const project = await this.getProject(projectId);
    const files = await this.getFilesByProject(projectId);
    const fileContents = [];
    
    // Get all file contents
    for (const file of files) {
      const contentVersions = await this.getAllFileVersions(file.id);
      fileContents.push(...contentVersions);
    }
    
    const components = await this.getComponentsByProject(projectId);
    const batches = await this.getBatchesByProject(projectId);
    const prompts = await this.getPromptsByProject(projectId);
    const responses = await this.getResponsesByProject(projectId);
    
    // Create exportable structure
    const exportData = {
      version: '1.0',
      exportDate: Date.now(),
      project,
      files,
      fileContents,
      components,
      batches,
      prompts,
      responses
    };
    
    // Convert to Blob for download
    return new Blob([JSON.stringify(exportData, null, 2)], {type: 'application/json'});
  }

  /**
   * Import a project from a JSON blob
   * @param {Blob} jsonBlob - Project export blob
   * @returns {Promise<string>} - Imported project ID
   */
  async importProject(jsonBlob) {
    try {
      // Parse the imported data
      const jsonText = await jsonBlob.text();
      const importData = JSON.parse(jsonText);
      
      // Validate structure
      if (!importData.project || !importData.files) {
        throw new Error('Invalid project format');
      }
      
      await this.ensureDbConnection();
      
      // Store all components in the database
      const transaction = this.db.transaction(
        ['projects', 'files', 'fileContents', 'components', 'batches', 'prompts', 'responses'],
        'readwrite'
      );
      
      // Store project
      const projectStore = transaction.objectStore('projects');
      await new Promise((resolve, reject) => {
        const request = projectStore.put(importData.project);
        request.onsuccess = () => resolve();
        request.onerror = (e) => reject(`Failed to import project: ${e.target.error}`);
      });
      
      // Store files
      const filesStore = transaction.objectStore('files');
      for (const file of importData.files) {
        await new Promise((resolve, reject) => {
          const request = filesStore.put(file);
          request.onsuccess = () => resolve();
          request.onerror = (e) => reject(`Failed to import file: ${e.target.error}`);
        });
      }
      
      // Store file contents
      const contentsStore = transaction.objectStore('fileContents');
      for (const content of importData.fileContents) {
        await new Promise((resolve, reject) => {
          const request = contentsStore.put(content);
          request.onsuccess = () => resolve();
          request.onerror = (e) => reject(`Failed to import file content: ${e.target.error}`);
        });
      }
      
      // Store components
      const componentsStore = transaction.objectStore('components');
      for (const component of importData.components) {
        await new Promise((resolve, reject) => {
          const request = componentsStore.put(component);
          request.onsuccess = () => resolve();
          request.onerror = (e) => reject(`Failed to import component: ${e.target.error}`);
        });
      }
      
      // Store batches
      const batchesStore = transaction.objectStore('batches');
      for (const batch of importData.batches) {
        await new Promise((resolve, reject) => {
          const request = batchesStore.put(batch);
          request.onsuccess = () => resolve();
          request.onerror = (e) => reject(`Failed to import batch: ${e.target.error}`);
        });
      }
      
      // Store prompts
      if (importData.prompts) {
        const promptsStore = transaction.objectStore('prompts');
        for (const prompt of importData.prompts) {
          await new Promise((resolve, reject) => {
            const request = promptsStore.put(prompt);
            request.onsuccess = () => resolve();
            request.onerror = (e) => reject(`Failed to import prompt: ${e.target.error}`);
          });
        }
      }
      
      // Store responses
      if (importData.responses) {
        const responsesStore = transaction.objectStore('responses');
        for (const response of importData.responses) {
          await new Promise((resolve, reject) => {
            const request = responsesStore.put(response);
            request.onsuccess = () => resolve();
            request.onerror = (e) => reject(`Failed to import response: ${e.target.error}`);
          });
        }
      }
      
      return importData.project.id;
    } catch (error) {
      console.error('Import error:', error);
      throw error;
    }
  }

  /**
   * Clear all data from the database
   * @returns {Promise<boolean>} - Success indicator
   */
  async clearAllData() {
    await this.ensureDbConnection();
    
    const stores = ['projects', 'files', 'fileContents', 'components', 'batches', 'prompts', 'responses'];
    
    for (const storeName of stores) {
      await new Promise((resolve, reject) => {
        const transaction = this.db.transaction([storeName], 'readwrite');
        const store = transaction.objectStore(storeName);
        const request = store.clear();
        
        request.onsuccess = () => resolve();
        request.onerror = (e) => reject(`Failed to clear ${storeName}: ${e.target.error}`);
      });
    }
    
    return true;
  }

  /**
   * Ensure database connection is established
   * @private
   */
  async ensureDbConnection() {
    if (!this.db) {
      await this.initDatabase();
    }
  }
}

export default StorageManager;
